import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { BookOpen, Heart, Eye, Sparkles, RefreshCw, Info } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';

// Signal definitions with icons and descriptions
const signalDefinitions = {
  narrativeDepth: {
    icon: BookOpen,
    description: 'How well the story layers meaning, subtext, and thematic complexity beyond surface-level plot.',
  },
  emotionalImpact: {
    icon: Heart,
    description: 'The film\'s ability to evoke genuine emotional responses and create lasting impressions.',
  },
  visualIdentity: {
    icon: Eye,
    description: 'Distinctiveness of cinematography, color palette, framing, and overall visual language.',
  },
  originality: {
    icon: Sparkles,
    description: 'How the film breaks conventions, introduces fresh ideas, or reimagines familiar territory.',
  },
  rewatchValue: {
    icon: RefreshCw,
    description: 'Whether the film rewards repeated viewings with new discoveries and sustained engagement.',
  },
};

// Get color based on score for intuitive understanding
const getScoreColor = (value) => {
  if (value >= 90) return { color: '#52ea88', label: 'Exceptional' };
  if (value >= 75) return { color: '#7aff9e', label: 'Strong' };
  if (value >= 60) return { color: '#a8e6cf', label: 'Good' };
  if (value >= 45) return { color: '#ffd93d', label: 'Mixed' };
  return { color: '#ff6b6b', label: 'Weak' };
};

// Calculate overall score
const calculateOverallScore = (signals) => {
  const values = Object.values(signals);
  return Math.round(values.reduce((a, b) => a + b, 0) / values.length);
};

// Circular progress indicator
const CircularScore = ({ value }) => {
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (value / 100) * circumference;
  const { color, label } = getScoreColor(value);

  return (
    <div className="relative w-32 h-32 mx-auto">
      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
        {/* Background circle */}
        <circle
          cx="50"
          cy="50"
          r="45"
          stroke="#1a4a2e"
          strokeWidth="6"
          fill="none"
        />
        {/* Progress circle */}
        <circle
          cx="50"
          cy="50"
          r="45"
          stroke={color}
          strokeWidth="6"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      {/* Center text */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold text-[#E6FFF6]">{value}</span>
        <span className="text-xs text-[#9FBFB4] uppercase tracking-wider">{label}</span>
      </div>
    </div>
  );
};

// Enhanced signal bar with icon and tooltip
const SignalBar = ({ signalKey, label, value, delay = 0 }) => {
  const { icon: Icon, description } = signalDefinitions[signalKey] || {};
  const { color } = getScoreColor(value);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-[#52ea88]" />}
          <span className="text-[#E6FFF6] text-sm font-medium">{label}</span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button className="text-[#9FBFB4] hover:text-[#52ea88] transition-colors">
                  <Info className="w-3 h-3" />
                </button>
              </TooltipTrigger>
              <TooltipContent 
                side="top" 
                className="max-w-[250px] bg-[#0B0F0E] border border-[#52ea88]/30 text-[#E6FFF6] text-xs p-3"
              >
                {description}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <span className="text-sm font-mono" style={{ color }}>{value}</span>
      </div>
      {/* Progress bar */}
      <div className="h-2 bg-[#0B0F0E] rounded-sm overflow-hidden">
        <div
          className="h-full rounded-sm transition-all duration-1000 ease-out"
          style={{
            width: `${value}%`,
            backgroundColor: color,
            animationDelay: `${delay}ms`,
          }}
        />
      </div>
    </div>
  );
};

// Score legend
const ScoreLegend = () => {
  const levels = [
    { range: '90-100', label: 'Exceptional', color: '#52ea88' },
    { range: '75-89', label: 'Strong', color: '#7aff9e' },
    { range: '60-74', label: 'Good', color: '#a8e6cf' },
    { range: '45-59', label: 'Mixed', color: '#ffd93d' },
    { range: '0-44', label: 'Weak', color: '#ff6b6b' },
  ];

  return (
    <div className="grid grid-cols-5 gap-1 mt-4 pt-4 border-t border-[#52ea88]/10">
      {levels.map((level) => (
        <div key={level.label} className="text-center">
          <div 
            className="w-full h-1.5 rounded-sm mb-1"
            style={{ backgroundColor: level.color }}
          />
          <span className="text-[10px] text-[#9FBFB4]">{level.label}</span>
        </div>
      ))}
    </div>
  );
};

const MatrixSignals = ({ signals, compact = false, showLegend = true }) => {
  const { t } = useLanguage();

  const signalLabels = {
    narrativeDepth: t('signals.narrativeDepth'),
    emotionalImpact: t('signals.emotionalImpact'),
    visualIdentity: t('signals.visualIdentity'),
    originality: t('signals.originality'),
    rewatchValue: t('signals.rewatchValue'),
  };

  if (!signals) return null;

  const overallScore = calculateOverallScore(signals);

  return (
    <div className={`${compact ? 'p-4' : 'p-6'} bg-[#121917] border border-[#52ea88]/15`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className={`text-[#52ea88] font-semibold ${compact ? 'text-sm' : 'text-lg'} uppercase tracking-wider`}>
          {t('sections.signals')}
        </h3>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button className="text-[#9FBFB4] hover:text-[#52ea88] transition-colors">
                <Info className="w-4 h-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent 
              side="left" 
              className="max-w-[280px] bg-[#0B0F0E] border border-[#52ea88]/30 text-[#E6FFF6] text-xs p-3"
            >
              Matrix Signals measure a film's impact across five key dimensions. 
              Unlike traditional ratings, these signals capture what makes cinema meaningful.
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Overall Score Circle */}
      {!compact && (
        <div className="mb-8">
          <CircularScore value={overallScore} />
          <p className="text-center text-[#9FBFB4] text-xs mt-3 uppercase tracking-wider">
            Overall Matrix Score
          </p>
        </div>
      )}

      {/* Individual Signals */}
      <div className="space-y-5">
        {Object.entries(signals).map(([key, value], index) => (
          <SignalBar
            key={key}
            signalKey={key}
            label={signalLabels[key]}
            value={value}
            delay={index * 100}
          />
        ))}
      </div>

      {/* Score Legend */}
      {showLegend && !compact && <ScoreLegend />}
    </div>
  );
};

export default MatrixSignals;
